import { useEffect, useRef, useState } from "react";
import { openEventStream } from "../api/sseClient";
import {
  ControlPipelineStatusSchema,
  ControlSessionPipelineStatusSchema,
  DocumentPipelineStatus,
  WorkStepSessionPipelineStatusSchema,
} from "../models/audit/documentPipelineStatus";
import { DerivedDocumentState } from "../models/audit/documentProcessingStatus";

const RECONNECT_DELAY_MS = 2000;

/** The subject a pipeline stream is keyed to: a control session's uploads, a
 * work-step session's uploads, or a control's own context documents (the
 * revise panel). */
export type PipelineScope = "control-session" | "control" | "work-step-session";

interface PipelineStatusSnapshot {
  derivedState: DerivedDocumentState;
  documents: DocumentPipelineStatus[];
  source: "hatchet" | "fallback";
}

interface UseControlSessionPipelineStreamResult {
  status: PipelineStatusSnapshot | null;
  documents: DocumentPipelineStatus[];
  overallState: DerivedDocumentState | null;
  isStreaming: boolean;
  error: string | null;
}

/**
 * Subscribes to the live per-document/per-step processing pipeline for a
 * control session over SSE. Re-validates each snapshot with Zod, surfaces the
 * latest document statuses, and reconnects with a fixed backoff if the
 * connection drops before the pipeline reaches a terminal state.
 *
 * Pass `reconnectKey` (e.g. an upload nonce) to force a fresh connection after
 * a terminal `complete` — for instance when new documents are uploaded.
 */
export function useControlSessionPipelineStream(
  subjectId: string | null,
  reconnectKey: number = 0,
  scope: PipelineScope = "control-session",
): UseControlSessionPipelineStreamResult {
  const [status, setStatus] = useState<PipelineStatusSnapshot | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!subjectId) return;

    let cancelled = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = async () => {
      if (cancelled) return;
      const controller = new AbortController();
      abortRef.current = controller;
      setIsStreaming(true);
      let completed = false;
      let terminalHttpError = false;

      const streamPath =
        scope === "control"
          ? `/controls/v2/control/${subjectId}/document-processing-status/stream`
          : scope === "work-step-session"
            ? `/work-steps/work-step-session/${subjectId}/document-processing-status/stream`
            : `/controls/v2/control-session/${subjectId}/document-processing-status/stream`;
      await openEventStream(
        streamPath,
        { method: "GET" },
        {
          onEvent: (event, data) => {
            if (event === "status" || event === "complete") {
              try {
                const schema =
                  scope === "control"
                    ? ControlPipelineStatusSchema
                    : scope === "work-step-session"
                      ? WorkStepSessionPipelineStatusSchema
                      : ControlSessionPipelineStatusSchema;
                const parsed = schema.parse(JSON.parse(data));
                setStatus({
                  derivedState: parsed.derivedState,
                  documents: parsed.documents,
                  source: parsed.source,
                });
                setError(null);
              } catch (e) {
                console.error("Failed to parse pipeline status", e);
              }
              if (event === "complete") completed = true;
            } else if (event === "error") {
              try {
                setError(JSON.parse(data).error ?? "Stream error");
              } catch {
                setError("Stream error");
              }
            }
            // "heartbeat" events only keep the connection warm; ignore.
          },
          onError: (msg, status) => {
            if (cancelled) return;
            setError(msg);
            // A 4xx is deterministic — the endpoint doesn't exist for us (not yet
            // deployed, bad id, no access) and retrying every 2s hammers it forever.
            // 5xx/network failures stay retryable.
            if (status !== undefined && status >= 400 && status < 500) {
              terminalHttpError = true;
            }
          },
        },
        controller.signal,
      );

      if (cancelled) return;
      setIsStreaming(false);
      // The server closes the stream once all documents are terminal. Otherwise
      // a drop is unexpected — reconnect after a short delay, unless the failure
      // was a deterministic client error.
      if (!completed && !terminalHttpError) {
        reconnectTimer = setTimeout(() => void connect(), RECONNECT_DELAY_MS);
      }
    };

    void connect();

    return () => {
      cancelled = true;
      abortRef.current?.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [subjectId, reconnectKey, scope]);

  return {
    status,
    documents: status?.documents ?? [],
    overallState: status?.derivedState ?? null,
    isStreaming,
    error,
  };
}
